import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useDropzone } from 'react-dropzone';
import { Upload, Music, X, AlertCircle, CheckCircle, Youtube, Link, Zap } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function FileUploader({ token, onUploadComplete, folders = [], activeFolderId = 'all' }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('file'); // 'file' or 'youtube'
  const [youtubeUrl, setYoutubeUrl] = useState('');
  const [youtubeImporting, setYoutubeImporting] = useState(false);
  const [asyncMode, setAsyncMode] = useState(false); // New: async batch mode
  // Processing tier — Tagging only (1) is the default for blazing speed; users opt
  // into Description (2) and Lyrics (3). Cost = tier value, in credits per track.
  const [tier, setTier] = useState(() => {
    const saved = parseInt(localStorage.getItem('syncpulse_tier') || '1', 10);
    return [1, 2, 3].includes(saved) ? saved : 1;
  });
  useEffect(() => { localStorage.setItem('syncpulse_tier', String(tier)); }, [tier]);
  // Destination folder for new uploads. null = Uncategorized. Defaults to current view
  // when it's a real folder, otherwise null.
  const [targetFolderId, setTargetFolderId] = useState(
    activeFolderId && activeFolderId !== 'all' && activeFolderId !== 'uncategorized' ? activeFolderId : null
  );

  // Track in-flight AbortControllers keyed by file id (for per-row cancel)
  const abortControllersRef = useRef({});
  // Track file ids the user explicitly cancelled, so AbortError isn't mistaken for a timeout
  const cancelledIdsRef = useRef(new Set());

  const cancelUpload = (fileId) => {
    cancelledIdsRef.current.add(fileId);
    const ctrl = abortControllersRef.current[fileId];
    if (ctrl) {
      try { ctrl.abort(); } catch { /* already aborted */ }
    }
    // Optimistically flip UI — the worker will confirm shortly
    setFiles(prev => prev.map(x => x.id === fileId ? { ...x, status: 'cancelled' } : x));
  };

  // Sync target when the active folder changes externally
  useEffect(() => {
    if (activeFolderId && activeFolderId !== 'all' && activeFolderId !== 'uncategorized') {
      setTargetFolderId(activeFolderId);
    }
  }, [activeFolderId]);

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    setError('');
    
    if (rejectedFiles.length > 0) {
      setError('Only MP3 files are accepted. Cyanite does not support WAV or other formats.');
    }

    const audioFiles = acceptedFiles.filter(file => 
      file.type === 'audio/mpeg' || 
      /\.mp3$/i.test(file.name)
    );

    // Add files to list immediately with 'validating' status for instant UI feedback
    const newEntries = audioFiles.map(file => ({
      file,
      id: Math.random().toString(36).substr(2, 9),
      status: 'validating'
    }));
    setFiles(prev => [...prev, ...newEntries]);

    // Validate each file (duration + size check) - runs in parallel
    const validationResults = await Promise.all(
      newEntries.map(async (entry) => {
        const result = await validateMp3(entry.file);
        return { id: entry.id, ...result };
      })
    );

    const invalidList = validationResults.filter(r => !r.valid);
    if (invalidList.length > 0) {
      const sample = invalidList[0];
      setError(`${invalidList.length} file(s) rejected: ${sample.error}`);
    }

    // Update statuses: invalid -> 'error', valid -> 'pending'
    setFiles(prev => prev.map(f => {
      const result = validationResults.find(r => r.id === f.id);
      if (!result) return f;
      if (result.valid) return { ...f, status: 'pending', duration: result.duration };
      return { ...f, status: 'error', errorMsg: result.error };
    }));
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'audio/mpeg': ['.mp3']
    },
    multiple: true
  });

  const removeFile = (id) => {
    setFiles(files.filter(f => f.id !== id));
  };

  // Validate MP3 duration and size client-side to avoid wasting Cyanite credits on invalid files.
  // Cyanite limits: MP3 only, max 20MB, max 15 min duration.
  const validateMp3 = (file) => {
    return new Promise((resolve) => {
      const MAX_DURATION = 15 * 60; // 15 minutes in seconds
      const MAX_SIZE = 20 * 1024 * 1024; // 20 MB
      if (file.size > MAX_SIZE) {
        resolve({ valid: false, error: `${(file.size / 1024 / 1024).toFixed(1)}MB exceeds 20MB Cyanite limit` });
        return;
      }
      const audio = document.createElement('audio');
      const objectUrl = URL.createObjectURL(file);
      audio.preload = 'metadata';
      const timeout = setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
        resolve({ valid: false, error: 'Could not read audio metadata (invalid MP3?)' });
      }, 10000);
      audio.onloadedmetadata = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(objectUrl);
        if (!isFinite(audio.duration) || audio.duration <= 0) {
          resolve({ valid: false, error: 'Invalid MP3 - no readable duration' });
        } else if (audio.duration > MAX_DURATION) {
          const mins = (audio.duration / 60).toFixed(1);
          resolve({ valid: false, error: `${mins} min exceeds 15-min Cyanite limit` });
        } else {
          resolve({ valid: true, duration: audio.duration });
        }
      };
      audio.onerror = () => {
        clearTimeout(timeout);
        URL.revokeObjectURL(objectUrl);
        resolve({ valid: false, error: 'Not a valid MP3 file' });
      };
      audio.src = objectUrl;
    });
  };

  const uploadFiles = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setError('');

    // Both sync and async modes use per-file parallel uploads to avoid ingress body-size limits.
    // The only difference: sync hits /api/tracks/upload (polling-driven), async hits a single-file
    // async endpoint (webhook-driven). Single-file mode still supports either endpoint cleanly.
    const useAsyncEndpoint = asyncMode && files.length > 1;
    const endpoint = useAsyncEndpoint ? '/api/tracks/batch-upload-async' : '/api/tracks/upload';
    const formFieldName = useAsyncEndpoint ? 'files' : 'file';

    const pendingFiles = files.filter(f => f.status === 'pending');
    const CONCURRENCY = 5;
    const MAX_RETRIES = 5;
    const fileStatus = {}; // id -> 'uploaded' | { error: string }

    // Per-file hard timeout. Cloudflare kills idle connections at ~100s.
    // Our backend upload endpoint returns immediately after streaming the file to disk,
    // so anything >90s means something is stuck → abort and retry.
    const UPLOAD_TIMEOUT_MS = 90000;

    const uploadOne = async (f) => {
      let attempt = 0;
      while (attempt < MAX_RETRIES) {
        attempt += 1;
        // Bail immediately if the user cancelled this row (even before the fetch starts)
        if (cancelledIdsRef.current.has(f.id)) {
          fileStatus[f.id] = { error: 'Cancelled by user', cancelled: true };
          return;
        }
        // Flip this file to 'uploading' so the user sees live progress per row
        fileStatus[f.id] = 'uploading';
        setFiles(prev => prev.map(x => x.id === f.id ? { ...x, status: 'uploading' } : x));
        const controller = new AbortController();
        abortControllersRef.current[f.id] = controller;
        const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS);
        try {
          const formData = new FormData();
          formData.append(formFieldName, f.file);
          if (targetFolderId) formData.append('folder_id', targetFolderId);
          formData.append('tier', String(tier));
          const response = await fetch(`${API_URL}${endpoint}`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
            signal: controller.signal
          });
          clearTimeout(timer);
          if (response.ok) {
            fileStatus[f.id] = 'uploaded';
            return;
          }
          // Credits exhausted -> fail fast with clear message, don't retry
          if (response.status === 402) {
            const txt = await response.text();
            let msg = 'Insufficient credits - top up in the admin dashboard to continue';
            try { msg = JSON.parse(txt).detail || msg; } catch { /* keep default */ }
            fileStatus[f.id] = { error: msg, code: 402 };
            return;
          }
          // Retry on transient errors (Cloudflare 5xx family + rate limit + gateway errors)
          const transient = [408, 425, 429, 500, 502, 503, 504, 520, 521, 522, 523, 524, 525];
          if (transient.includes(response.status) && attempt < MAX_RETRIES) {
            // Exponential backoff with jitter: 2s, 4s, 8s, 16s, 32s + random 0-1s
            const backoff = Math.min(32000, 2000 * Math.pow(2, attempt - 1)) + Math.random() * 1000;
            await new Promise(r => setTimeout(r, backoff));
            continue;
          }
          const txt = await response.text();
          let msg = `HTTP ${response.status}`;
          try { msg = JSON.parse(txt).detail || msg; } catch { /* HTML error */ }
          fileStatus[f.id] = { error: msg };
          return;
        } catch (e) {
          clearTimeout(timer);
          // User clicked cancel on this row -> finalize as cancelled, skip retries
          if (cancelledIdsRef.current.has(f.id)) {
            fileStatus[f.id] = { error: 'Cancelled by user', cancelled: true };
            return;
          }
          // AbortError not triggered by user = our 90s timeout. Treat as retryable transient.
          const isTimeout = e.name === 'AbortError';
          if (attempt < MAX_RETRIES) {
            const backoff = Math.min(32000, 2000 * Math.pow(2, attempt - 1)) + Math.random() * 1000;
            await new Promise(r => setTimeout(r, backoff));
            continue;
          }
          fileStatus[f.id] = { error: isTimeout ? 'Upload timed out (server did not respond in 90s)' : (e.message || 'Network error') };
          return;
        } finally {
          delete abortControllersRef.current[f.id];
        }
      }
    };

    try {
      // Worker-pool pattern: N workers pulling from a shared queue
      const queue = [...pendingFiles];
      const worker = async () => {
        while (queue.length > 0) {
          const f = queue.shift();
          if (!f) return;
          await uploadOne(f);
          // Incrementally update UI as each file completes
          setFiles(prev => prev.map(x => {
            const s = fileStatus[x.id];
            if (s === 'uploaded') return { ...x, status: 'uploaded' };
            if (s === 'uploading') return { ...x, status: 'uploading' };
            if (s && s.cancelled) return { ...x, status: 'cancelled' };
            if (s && s.error) return { ...x, status: 'error' };
            return x;
          }));
        }
      };
      await Promise.all(Array.from({ length: CONCURRENCY }, worker));

      // Exclude user-cancelled rows from the "failed" error banner
      const failures = Object.values(fileStatus).filter(s => s && s.error && !s.cancelled);
      const cancelledCount = Object.values(fileStatus).filter(s => s && s.cancelled).length;
      const creditFailures = failures.filter(f => f.code === 402);
      if (failures.length > 0) {
        if (creditFailures.length > 0) {
          setError(`❗ Out of credits — ${creditFailures.length}/${pendingFiles.length} files rejected. Top up credits to continue. ${failures.length > creditFailures.length ? `(${failures.length - creditFailures.length} others failed with other errors)` : ''}`);
        } else {
          setError(`${failures.length}/${pendingFiles.length} uploads failed: ${failures[0].error}`);
        }
        // Keep failed rows visible so user can see which failed; clear after delay
        setTimeout(() => { setFiles([]); cancelledIdsRef.current.clear(); onUploadComplete?.(); }, 4000);
      } else if (cancelledCount > 0 && cancelledCount === pendingFiles.length) {
        // All cancelled - clear quietly without error banner
        setTimeout(() => { setFiles([]); cancelledIdsRef.current.clear(); onUploadComplete?.(); }, 1500);
      } else {
        setTimeout(() => { setFiles([]); cancelledIdsRef.current.clear(); onUploadComplete?.(); }, 1500);
      }
    } catch (err) {
      // Unexpected error - always reset state so user can retry
      setError(err.message || 'Upload failed unexpectedly');
      setFiles(prev => prev.map(f => f.status === 'pending' ? { ...f, status: 'error' } : f));
    } finally {
      setUploading(false); // ALWAYS reset - prevents stuck uploader after failures
    }
  };

  const importYoutube = async () => {
    if (!youtubeUrl.trim()) return;
    
    setYoutubeImporting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/tracks/youtube`, {
        method: 'POST',
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ youtube_url: youtubeUrl.trim() })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'YouTube import failed');
      }

      setYoutubeUrl('');
      onUploadComplete?.();

    } catch (err) {
      setError(err.message);
    }

    setYoutubeImporting(false);
  };

  return (
    <div className="bg-card border border-border rounded-xl p-6" data-testid="file-uploader">
      {/* Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <button
          onClick={() => setActiveTab('file')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'file' 
              ? 'bg-primary text-white' 
              : 'bg-black/5 text-muted-foreground hover:bg-black/10'
          }`}
          data-testid="tab-file-upload"
        >
          <Upload className="w-4 h-4" />
          Upload Files
        </button>
        <button
          onClick={() => setActiveTab('youtube')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
            activeTab === 'youtube' 
              ? 'bg-primary text-white' 
              : 'bg-black/5 text-muted-foreground hover:bg-black/10'
          }`}
          data-testid="tab-youtube-import"
        >
          <Youtube className="w-4 h-4" />
          YouTube Import
        </button>
      </div>

      {/* File Upload Tab */}
      {activeTab === 'file' && (
        <>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-bold text-foreground">Upload Audio Files</h3>
            {files.length > 0 && (
              <span className="text-sm text-muted-foreground">{files.length} file(s) selected</span>
            )}
          </div>

          {/* Dropzone */}
          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
              isDragActive 
                ? 'border-primary bg-primary/5' 
                : 'border-border hover:border-primary/50 hover:bg-black/5'
            }`}
            data-testid="dropzone"
          >
            <input {...getInputProps()} data-testid="file-input" />
            <div className="flex flex-col items-center">
              <div className={`w-16 h-16 rounded-full flex items-center justify-center mb-4 transition-colors ${
                isDragActive ? 'bg-primary/10' : 'bg-muted'
              }`}>
                <Upload className={`w-7 h-7 ${isDragActive ? 'text-primary' : 'text-muted-foreground'}`} />
              </div>
              <p className="text-foreground font-medium mb-1">
                {isDragActive ? 'Drop your MP3 files here' : 'Drag & drop MP3 files here'}
              </p>
              <p className="text-sm text-muted-foreground">
                or click to browse • MP3 Only
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                (Do not upload WAV or other formats - not supported)
              </p>
            </div>
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-4 space-y-2" data-testid="file-list">
              {files.map(({ file, id, status }) => (
                <div 
                  key={id} 
                  className={`flex items-center justify-between p-3 rounded-xl transition-colors ${
                    status === 'uploaded' ? 'bg-green-50 border border-green-200' :
                    status === 'error' ? 'bg-red-50 border border-red-200' :
                    'bg-black/5'
                  }`}
                  data-testid={`file-item-${id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      status === 'uploaded' ? 'bg-green-100' :
                      status === 'uploading' ? 'bg-blue-100' :
                      status === 'cancelled' ? 'bg-gray-100' :
                      status === 'error' ? 'bg-red-100' :
                      'bg-muted'
                    }`}>
                      {status === 'uploaded' ? (
                        <CheckCircle className="w-4 h-4 text-green-600" />
                      ) : status === 'uploading' ? (
                        <div className="w-4 h-4 border-2 border-blue-300 border-t-blue-600 rounded-full animate-spin" />
                      ) : status === 'cancelled' ? (
                        <X className="w-4 h-4 text-gray-500" />
                      ) : (
                        <Music className={`w-4 h-4 ${status === 'error' ? 'text-red-600' : 'text-muted-foreground'}`} />
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className={`text-sm font-medium truncate ${status === 'cancelled' ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB
                        {status === 'uploading' && <span className="ml-2 text-blue-600 font-medium">Uploading…</span>}
                        {status === 'cancelled' && <span className="ml-2 text-gray-500 font-medium">Cancelled</span>}
                      </p>
                    </div>
                  </div>
                  
                  {status === 'pending' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); removeFile(id); }}
                      className="p-1.5 rounded-lg hover:bg-black/10 transition-colors"
                      data-testid={`remove-file-${id}`}
                    >
                      <X className="w-4 h-4 text-muted-foreground" />
                    </button>
                  )}
                  {status === 'uploading' && (
                    <button
                      onClick={(e) => { e.stopPropagation(); cancelUpload(id); }}
                      className="p-1.5 rounded-lg hover:bg-red-50 transition-colors group"
                      title="Cancel upload"
                      data-testid={`cancel-upload-${id}`}
                    >
                      <X className="w-4 h-4 text-muted-foreground group-hover:text-red-600 transition-colors" />
                    </button>
                  )}
                </div>
              ))}

              {/* Destination folder picker */}
              <div className="flex items-center justify-between p-3 rounded-xl bg-white/5 border border-white/10">
                <div className="flex items-center gap-2">
                  <Music className="w-4 h-4 text-white/60" />
                  <p className="text-sm text-white/80">Upload to</p>
                </div>
                <select
                  value={targetFolderId || ''}
                  onChange={(e) => setTargetFolderId(e.target.value || null)}
                  className="px-3 py-1.5 input-dark rounded-lg text-sm appearance-none cursor-pointer"
                  data-testid="upload-destination-select"
                >
                  <option value="">Uncategorized</option>
                  {folders.map(f => (
                    <option key={f.id} value={f.id}>{f.name}</option>
                  ))}
                </select>
              </div>

              {/* Processing Tier Selector — Tagging is mandatory; user opts into Description / Lyrics */}
              <div className="p-3 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20 space-y-2">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-medium text-foreground">Processing tier</p>
                  <span className="text-xs text-muted-foreground">{tier} credit{tier > 1 ? 's' : ''} / track</span>
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="tier-selector">
                  {[
                    { value: 1, label: 'Tagging', sub: '~30s · 1 cr' },
                    { value: 2, label: '+ Description', sub: '~60s · 2 cr' },
                    { value: 3, label: '+ Lyrics', sub: '~90s · 3 cr' },
                  ].map(opt => (
                    <button
                      key={opt.value}
                      onClick={() => setTier(opt.value)}
                      data-testid={`tier-option-${opt.value}`}
                      className={`px-2 py-2 rounded-lg text-xs font-semibold border transition-all ${
                        tier === opt.value
                          ? 'bg-primary text-white border-primary shadow'
                          : 'bg-white/40 text-foreground border-white/40 hover:bg-white/60'
                      }`}
                    >
                      <div>{opt.label}</div>
                      <div className={`text-[10px] mt-0.5 ${tier === opt.value ? 'text-white/80' : 'text-muted-foreground'}`}>{opt.sub}</div>
                    </button>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground">
                  You can add description or lyrics later from any track row — pay only when you need them.
                </p>
              </div>

              {/* Async Mode Toggle - show when multiple files */}
              {files.length > 1 && (
                <div className="flex items-center justify-between p-3 rounded-xl bg-gradient-to-r from-primary/5 to-primary/10 border border-primary/20">
                  <div className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-primary" />
                    <div>
                      <p className="text-sm font-medium text-foreground">Async Batch Mode</p>
                      <p className="text-xs text-muted-foreground">Faster for large batches</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAsyncMode(!asyncMode)}
                    className={`relative w-11 h-6 rounded-full transition-colors ${
                      asyncMode ? 'bg-primary' : 'bg-gray-300'
                    }`}
                    data-testid="async-mode-toggle"
                  >
                    <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${
                      asyncMode ? 'translate-x-5' : 'translate-x-0'
                    }`} />
                  </button>
                </div>
              )}

              {/* Upload Button */}
              <button
                onClick={uploadFiles}
                disabled={uploading || files.every(f => f.status !== 'pending')}
                className="w-full mt-4 py-3 bg-primary text-white font-semibold rounded-xl btn-lift flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="upload-button"
              >
                {uploading ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    {(() => {
                      const total = files.filter(f => f.status !== 'error' && f.status !== 'uploaded' ? true : f.status === 'uploaded').length;
                      const done = files.filter(f => f.status === 'uploaded').length;
                      const active = files.filter(f => f.status === 'uploading').length;
                      if (total > 1) return `Uploading ${done}/${total}${active > 0 ? ` (${active} in flight)` : ''}…`;
                      return 'Uploading…';
                    })()}
                  </>
                ) : (
                  <>
                    <Upload className="w-5 h-5" />
                    Upload {files.filter(f => f.status === 'pending').length} File(s)
                  </>
                )}
              </button>
            </div>
          )}
        </>
      )}

      {/* YouTube Import Tab */}
      {activeTab === 'youtube' && (
        <>
          <div className="mb-4">
            <h3 className="font-heading text-lg font-bold text-foreground">Import from YouTube</h3>
            <p className="text-sm text-muted-foreground mt-1">
              Paste a YouTube link to analyze the audio and generate metadata
            </p>
          </div>

          <div className="flex gap-3">
            <div className="flex-1 relative">
              <Link className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="url"
                value={youtubeUrl}
                onChange={(e) => setYoutubeUrl(e.target.value)}
                placeholder="https://www.youtube.com/watch?v=..."
                className="w-full pl-11 pr-4 py-3 bg-white border border-border rounded-xl focus:border-primary transition-colors text-gray-900"
                data-testid="youtube-url-input"
              />
            </div>
            <button
              onClick={importYoutube}
              disabled={youtubeImporting || !youtubeUrl.trim()}
              className="px-6 py-3 bg-primary text-white font-semibold rounded-xl btn-lift flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90 transition-opacity"
              data-testid="youtube-import-button"
            >
              {youtubeImporting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Youtube className="w-5 h-5" />
                  Import
                </>
              )}
            </button>
          </div>

          <div className="mt-4 p-4 bg-primary/10 border border-primary/20 rounded-xl">
            <div className="flex items-start gap-3">
              <Youtube className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-foreground mb-1">YouTube Import Notes:</p>
                <ul className="text-muted-foreground space-y-1">
                  <li>• Audio will be analyzed directly from YouTube</li>
                  <li>• Lyrics transcription is not available for YouTube imports</li>
                  <li>• Works with any public YouTube video</li>
                  <li>• Processing may take 1-3 minutes</li>
                </ul>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Error Message */}
      {error && (
        <div className="mt-4 p-3 bg-destructive/10 border border-destructive/20 rounded-xl flex items-center gap-2 text-destructive" data-testid="upload-error">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          <p className="text-sm">{error}</p>
        </div>
      )}
    </div>
  );
}
